<template>
    <div class="format-select" :class="inputClass">
        <label v-if="fixedLabel" class="settings-field-label" :for="`format-select-${_uid}`">{{ label }}</label>
        <v-menu
            v-model="menuOpen"
            :close-on-content-click="false"
            offset-y
            max-width="360"
            min-width="300"
            :disabled="disabled || loading"
            content-class="format-select__menu"
        >
            <template #activator="{ on, attrs }">
                <v-text-field
                    ref="field"
                    :value="closedDisplayText"
                    readonly
                    :id="fixedLabel ? `format-select-${_uid}` : undefined"
                    :label="fixedLabel ? null : label"
                    :placeholder="placeholder || (fixedLabel ? label : undefined)"
                    :loading="loading"
                    :disabled="disabled"
                    :clearable="false"
                    :dense="dense"
                    :outlined="outlined"
                    :filled="filled"
                    :hide-details="hideDetails"
                    :error-messages="errorMessages"
                    :hint="hint"
                    :persistent-hint="persistentHint"
                    :autofocus="autofocus"
                    :append-icon="appendIcon"
                    v-bind="attrs"
                    v-on="on"
                    @click:append="onAppendClick"
                    @keydown="onFieldKeydown"
                    @blur="$emit('blur')"
                />
            </template>

            <v-card class="format-select__panel">
                <div
                    v-if="kindFirst && step === 'kind'"
                    class="format-select__step"
                >
                    <v-list dense class="py-0 format-select__list">
                        <v-list-item
                            v-for="kind in kindOptions"
                            :key="kind.id"
                            :class="{ 'v-list-item--active': kind.selected }"
                            @click="onKindSelect(kind)"
                        >
                            <v-list-item-title>
                                {{ kind.text }}
                            </v-list-item-title>
                            <v-list-item-action class="my-0">
                                <v-icon
                                    v-if="kind.selected"
                                    small
                                    color="primary"
                                >
                                    mdi-check
                                </v-icon>
                                <v-icon
                                    v-else-if="!kind.immediate"
                                    small
                                >
                                    mdi-chevron-right
                                </v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </div>

                <div
                    v-else-if="kindFirst && step === 'units'"
                    class="format-select__step"
                >
                    <div class="d-flex align-center px-2 pt-1">
                        <v-btn
                            v-if="showKindBack"
                            icon
                            x-small
                            class="mr-1"
                            @click="backToKinds"
                        >
                            <v-icon small>mdi-arrow-left</v-icon>
                        </v-btn>
                        <div class="text-body-2 font-weight-medium">
                            {{ detailKindLabel }}
                        </div>
                    </div>
                    <v-list dense class="py-0 format-select__list">
                        <v-list-item
                            v-for="unit in detailKindUnits"
                            :key="String(unit.value)"
                            :class="{
                                'v-list-item--active':
                                    unit.value === selectedKindUnit,
                            }"
                            @click="onKindUnitSelect(unit.value)"
                        >
                            <v-list-item-title>{{ unit.text }}</v-list-item-title>
                            <v-list-item-action
                                v-if="unit.value === selectedKindUnit"
                                class="my-0"
                            >
                                <v-icon small color="primary">mdi-check</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </div>

                <template v-else>
                    <div
                        v-if="!kindFirst && actionItems.length && !isFormatSearchActive"
                        class="format-select__actions"
                    >
                        <v-list dense class="py-0">
                            <v-list-item
                                v-for="action in actionItems"
                                :key="'action-' + String(action.value)"
                                @click="onActionSelect(action.value)"
                            >
                                <v-list-item-title>
                                    {{ action.text }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                        <v-divider />
                    </div>

                    <div
                        v-if="step === 'container'"
                        class="format-select__step"
                    >
                        <div
                            v-if="kindFirst"
                            class="d-flex align-center px-2 pt-1"
                        >
                            <v-btn
                                v-if="showKindBack"
                                icon
                                x-small
                                class="mr-1"
                                @click="backToKinds"
                            >
                                <v-icon small>mdi-arrow-left</v-icon>
                            </v-btn>
                            <div class="text-body-2 font-weight-medium">
                                {{ $translate("kindFormat") }}
                            </div>
                        </div>
                        <div
                            v-if="!isFormatSearchActive"
                            class="format-select__step-title px-3 pt-2 pb-1 text-caption grey--text text--darken-1"
                        >
                            {{ $translate("pickContainer") }}
                        </div>
                        <v-text-field
                            v-if="showFormatSearchField"
                            ref="formatSearch"
                            v-model="containerSearch"
                            dense
                            hide-details
                            clearable
                            class="px-3 pb-1"
                            :placeholder="$translate('searchFormat')"
                            prepend-inner-icon="mdi-magnify"
                        />
                        <v-list
                            v-if="isFormatSearchActive && searchedFormatOptions.length"
                            dense
                            class="py-0 format-select__list"
                        >
                            <v-list-item
                                v-for="option in searchedFormatOptions"
                                :key="'format-' + option.key"
                                :class="{
                                    'v-list-item--active': option.selected,
                                }"
                                @click="onSearchedFormatSelect(option.format)"
                            >
                                <v-list-item-title>{{ option.label }}</v-list-item-title>
                                <v-list-item-action
                                    v-if="option.selected"
                                    class="my-0"
                                >
                                    <v-icon small color="primary">mdi-check</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                        <v-list
                            v-else-if="!isFormatSearchActive && containerOptions.length"
                            dense
                            class="py-0 format-select__list"
                        >
                            <v-list-item
                                v-for="option in containerOptions"
                                :key="option.key"
                                @click="onContainerSelect(option)"
                            >
                                <v-list-item-title>{{ option.label }}</v-list-item-title>
                                <v-list-item-action class="my-0">
                                    <v-icon small>mdi-chevron-right</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                        <div
                            v-else
                            class="px-3 py-3 text-caption grey--text"
                        >
                            {{ noDataText || $translate("noFormatFound") }}
                        </div>
                    </div>

                    <div
                        v-else
                        class="format-select__step"
                    >
                        <div class="d-flex align-center px-2 pt-1">
                            <v-btn
                                icon
                                x-small
                                class="mr-1"
                                @click="backToContainers"
                            >
                                <v-icon small>mdi-arrow-left</v-icon>
                            </v-btn>
                            <div class="text-body-2 font-weight-medium">
                                {{ selectedContainerLabel }}
                            </div>
                        </div>

                        <div
                            v-if="unitChoices.length > 1"
                            class="px-3 pt-2"
                        >
                            <div class="text-caption grey--text text--darken-1 mb-1">
                                {{ $translate("pickUnit") }}
                            </div>
                            <v-chip-group
                                :value="selectedUnit"
                                mandatory
                                column
                                @change="onUnitSelect"
                            >
                                <v-chip
                                    v-for="unit in unitChoices"
                                    :key="unit"
                                    :value="unit"
                                    filter
                                    small
                                    outlined
                                >
                                    {{ unit }}
                                </v-chip>
                            </v-chip-group>
                        </div>

                        <div class="format-select__step-title px-3 pt-2 pb-1 text-caption grey--text text--darken-1">
                            {{ $translate("pickQuantity") }}
                            <span v-if="selectedUnit && unitChoices.length === 1">
                                ({{ selectedUnit }})
                            </span>
                        </div>
                        <v-list
                            v-if="quantityChoices.length"
                            dense
                            class="py-0 format-select__list"
                        >
                            <v-list-item
                                v-for="quantity in quantityChoices"
                                :key="String(quantity)"
                                :class="{
                                    'v-list-item--active': isQuantitySelected(quantity),
                                }"
                                @click="onQuantitySelect(quantity)"
                            >
                                <v-list-item-title>
                                    {{ quantityChoiceLabel(quantity) }}
                                </v-list-item-title>
                                <v-list-item-action
                                    v-if="isQuantitySelected(quantity)"
                                    class="my-0"
                                >
                                    <v-icon small color="primary">mdi-check</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                        <div
                            v-else
                            class="px-3 py-3 text-caption grey--text"
                        >
                            {{ $translate("noQuantityForUnit") }}
                        </div>
                    </div>
                </template>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import GlobalMixins from "@/mixins/global";
import {
    FORMAT_SELECT_CREATE,
    FORMAT_SELECT_CUSTOM,
    FORMAT_SELECT_UNIT,
} from "@/constants/format-select";
import { groupPlainUnitsByKind } from "@/utils/formatCascade";

export default {
    name: "FormatSelect",
    mixins: [GlobalMixins],
    props: {
        fixedLabel: Boolean,
        value: {
            type: [Number, String],
            default: null,
        },
        formats: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: "",
        },
        placeholder: {
            type: String,
            default: "",
        },
        variant: {
            type: String,
            default: "plain",
            validator: (value) =>
                ["plain", "sale", "grouped", "code"].includes(value),
        },
        emptyOptionLabel: {
            type: String,
            default: null,
        },
        unitOptionLabel: {
            type: String,
            default: null,
        },
        showUnitOption: {
            type: Boolean,
            default: null,
        },
        showCustomOption: {
            type: Boolean,
            default: null,
        },
        showCreateOption: {
            type: Boolean,
            default: null,
        },
        formatFilter: {
            type: Function,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        dense: {
            type: Boolean,
            default: true,
        },
        outlined: {
            type: Boolean,
            default: true,
        },
        filled: {
            type: Boolean,
            default: false,
        },
        // Generic extra choices ({ text, value }) listed above the formats;
        // selecting one emits its raw value (e.g. a plain unit like "pc").
        actions: {
            type: Array,
            default: () => [],
        },
        // When true, choose nature first (piece / weight / volume / format)
        // then the detail step. Used by QuantityUnitSelect.
        kindFirst: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: [Boolean, String],
            default: "auto",
        },
        errorMessages: {
            type: Array,
            default: () => [],
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        richItems: {
            type: Boolean,
            default: false,
        },
        inputClass: {
            type: String,
            default: null,
        },
        noDataText: {
            type: String,
            default: null,
        },
        hint: {
            type: String,
            default: null,
        },
        persistentHint: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            menuOpen: false,
            step: "container",
            containerSearch: "",
            selectedContainerKey: null,
            selectedUnit: null,
            selectedQuantity: null,
            detailKind: null,
        };
    },
    computed: {
        resolvedShowUnitOption() {
            if (this.showUnitOption != null) {
                return this.showUnitOption;
            }
            return this.variant === "sale";
        },
        resolvedShowCustomOption() {
            if (this.showCustomOption != null) {
                return this.showCustomOption;
            }
            return this.variant === "grouped";
        },
        resolvedShowCreateOption() {
            if (this.showCreateOption != null) {
                return this.showCreateOption;
            }
            return (
                this.variant === "grouped"
                && this.userHasPermission("catalog.add_format")
            );
        },
        filteredFormats() {
            if (!this.formatFilter) {
                return this.formats || [];
            }
            return (this.formats || []).filter((format) =>
                this.formatFilter(format),
            );
        },
        actionItems() {
            const items = [...this.actions];
            if (this.variant === "code") {
                items.push({
                    text: this.emptyOptionLabel || "—",
                    value: "",
                });
            }
            if (this.resolvedShowCustomOption) {
                items.push({
                    text: this.$translate("customFormat"),
                    value: FORMAT_SELECT_CUSTOM,
                });
            }
            if (this.resolvedShowCreateOption) {
                items.push({
                    text: this.$translate("createFormat"),
                    value: FORMAT_SELECT_CREATE,
                });
            }
            if (this.resolvedShowUnitOption) {
                items.push({
                    text:
                        this.unitOptionLabel
                        || this.emptyOptionLabel
                        || this.$translate("defaultEntry"),
                    value: FORMAT_SELECT_UNIT,
                });
            }
            return items;
        },
        kindOptions() {
            if (!this.kindFirst) {
                return [];
            }
            const groups = groupPlainUnitsByKind(this.actions || []);
            const items = [];
            const pushKind = (id, text, units) => {
                if (!units.length) {
                    return;
                }
                items.push({
                    id,
                    text,
                    units,
                    immediate: units.length === 1,
                    selected: units.some((unit) => unit.value === this.value),
                });
            };
            pushKind("piece", this.$translate("kindPiece"), groups.piece);
            pushKind("weight", this.$translate("kindWeight"), groups.weight);
            pushKind("volume", this.$translate("kindVolume"), groups.volume);
            if (this.filteredFormats.length) {
                items.push({
                    id: "format",
                    text: this.$translate("kindFormat"),
                    units: [],
                    immediate: false,
                    selected: !!this.selectedFormat,
                });
            }
            return items;
        },
        showKindBack() {
            return this.kindFirst && this.kindOptions.length > 1;
        },
        detailKindUnits() {
            const kind = this.kindOptions.find((item) => item.id === this.detailKind);
            return kind?.units || [];
        },
        detailKindLabel() {
            const kind = this.kindOptions.find((item) => item.id === this.detailKind);
            return kind?.text || "";
        },
        selectedKindUnit() {
            if (
                this.detailKindUnits.some((unit) => unit.value === this.value)
            ) {
                return this.value;
            }
            return null;
        },
        containerOptions() {
            return this.$utils.buildFormatContainerOptions(this.filteredFormats);
        },
        showFormatSearchField() {
            return (
                this.containerOptions.length > 8
                || this.filteredFormats.length > 8
            );
        },
        isFormatSearchActive() {
            return String(this.containerSearch || "").trim().length > 0;
        },
        searchedFormatOptions() {
            const query = String(this.containerSearch || "").trim();
            if (!query) {
                return [];
            }
            const matched = this.filteredFormats.filter((format) => {
                const label = this.$utils.getFormatDisplayLabel(format, 1) || "";
                return this.$utils.filterFormatSelectItem(
                    {
                        value: this.variant === "code" ? format.code : format.id,
                        text: label,
                    },
                    query,
                    label,
                    this.filteredFormats,
                );
            });
            return this.$utils.sortFormatsByCapacity(matched).map((format) => {
                const value =
                    this.variant === "code" ? format.code : format.id;
                return {
                    key: String(value ?? format.id),
                    format,
                    label: this.$utils.getFormatDisplayLabel(format, 1),
                    selected: this.isSearchedFormatSelected(format),
                };
            });
        },
        selectedContainerLabel() {
            const match = this.containerOptions.find(
                (option) => option.key === this.selectedContainerKey,
            );
            return match?.label || "";
        },
        unitChoices() {
            if (!this.selectedContainerKey) {
                return [];
            }
            return this.$utils.unitsForContainer(
                this.filteredFormats,
                this.selectedContainerKey,
            );
        },
        quantityChoices() {
            if (!this.selectedContainerKey || !this.selectedUnit) {
                return [];
            }
            return this.$utils.quantitiesForContainerAndUnit(
                this.filteredFormats,
                this.selectedContainerKey,
                this.selectedUnit,
            );
        },
        selectedFormat() {
            if (this.value == null || this.value === "" || this.value === FORMAT_SELECT_UNIT) {
                return null;
            }
            const matchFormat = (format) =>
                this.variant === "code"
                    ? format.code === this.value
                    : format.id == this.value;
            return (
                this.filteredFormats.find(matchFormat)
                || (this.formats || []).find(matchFormat)
                || null
            );
        },
        isUnitValue() {
            return (
                this.resolvedShowUnitOption
                && (this.value == null || this.value === FORMAT_SELECT_UNIT)
            );
        },
        isEmptyCodeValue() {
            return this.variant === "code" && (this.value == null || this.value === "");
        },
        selectedAction() {
            if (this.value == null) {
                return null;
            }
            return (
                this.actions.find((action) => action.value === this.value)
                || null
            );
        },
        closedDisplayText() {
            if (this.selectedAction) {
                if (this.kindFirst) {
                    const pieceKind = this.kindOptions.find(
                        (kind) => kind.id === "piece",
                    );
                    if (
                        pieceKind
                        && pieceKind.units.length === 1
                        && pieceKind.units[0].value === this.selectedAction.value
                    ) {
                        return pieceKind.text;
                    }
                }
                return this.selectedAction.text;
            }
            if (this.isUnitValue) {
                return (
                    this.unitOptionLabel
                    || this.emptyOptionLabel
                    || this.$translate("defaultEntry")
                );
            }
            if (this.isEmptyCodeValue) {
                return this.emptyOptionLabel || "—";
            }
            if (this.selectedFormat) {
                return this.$utils.getFormatDisplayLabel(this.selectedFormat, 1);
            }
            return "";
        },
        hasValue() {
            if (this.variant === "code") {
                return this.value != null && this.value !== "";
            }
            if (this.resolvedShowUnitOption) {
                return this.value != null && this.value !== FORMAT_SELECT_UNIT;
            }
            return this.value != null && this.value !== "";
        },
        appendIcon() {
            if (this.clearable && this.hasValue) {
                return "mdi-close";
            }
            return "mdi-menu-down";
        },
    },
    watch: {
        menuOpen(open) {
            if (open) {
                this.resetPanelFromValue();
                this.focusFormatSearch();
            } else {
                this.containerSearch = "";
            }
        },
    },
    methods: {
        resetPanelFromValue() {
            this.containerSearch = "";
            if (this.kindFirst) {
                const format = this.selectedFormat;
                if (format) {
                    this.restoreSelectedFormatPanel(format);
                    return;
                }
                this.clearCascadeSelection();
                this.detailKind = null;
                this.resetKindQueue();
                return;
            }
            const format = this.selectedFormat;
            if (!format) {
                this.clearCascadeSelection();
                this.step = "container";
                return;
            }
            this.restoreSelectedFormatPanel(format);
        },
        clearCascadeSelection() {
            this.selectedContainerKey = null;
            this.selectedUnit = null;
            this.selectedQuantity = null;
        },
        /**
         * Re-open on the capacity step for the current format so sibling
         * contenances stay visible. Fall back to the container list when the
         * cascade cannot be rebuilt from the selected format.
         */
        restoreSelectedFormatPanel(format) {
            const formats = this.filteredFormats.length
                ? this.filteredFormats
                : (this.formats || []);
            let key = this.$utils.getFormatContainerKey(format);
            if (!this.containerOptions.some((option) => option.key === key)) {
                const sameId = formats.find((item) => item.id == format.id);
                key = sameId
                    ? this.$utils.getFormatContainerKey(sameId)
                    : key;
            }
            if (
                !key
                || !this.containerOptions.some((option) => option.key === key)
            ) {
                this.clearCascadeSelection();
                this.detailKind = this.kindFirst ? "format" : null;
                this.step = "container";
                return;
            }

            const units = this.$utils.unitsForContainer(formats, key);
            let unit = format.content_unit || null;
            if (
                unit
                && !units.some((item) => String(item) === String(unit))
            ) {
                unit =
                    units.find(
                        (item) =>
                            String(item).toLowerCase()
                            === String(unit).toLowerCase(),
                    ) || null;
            }
            if (!unit) {
                unit = units[0] || null;
            }

            const quantities = unit
                ? this.$utils.quantitiesForContainerAndUnit(formats, key, unit)
                : [];
            if (!unit || quantities.length === 0) {
                this.clearCascadeSelection();
                this.detailKind = this.kindFirst ? "format" : null;
                this.step = "container";
                return;
            }

            this.selectedContainerKey = key;
            this.selectedUnit = unit;
            this.selectedQuantity = format.content_quantity ?? null;
            this.detailKind = this.kindFirst ? "format" : null;
            this.step = "content";
        },
        resetKindQueue() {
            const kinds = this.kindOptions;
            if (kinds.length === 1) {
                this.enterKind(kinds[0], { auto: true });
                return;
            }
            this.step = "kind";
        },
        enterKind(kind, { auto = false } = {}) {
            if (!kind) {
                this.step = "kind";
                return;
            }
            if (kind.id === "format") {
                this.clearCascadeSelection();
                this.step = "container";
                return;
            }
            if (!auto && kind.units.length === 1) {
                this.onActionSelect(kind.units[0].value);
                return;
            }
            this.detailKind = kind.id;
            this.step = "units";
        },
        onKindSelect(kind) {
            this.enterKind(kind);
        },
        onKindUnitSelect(unitValue) {
            if (unitValue == null || unitValue === "") {
                return;
            }
            this.onActionSelect(unitValue);
        },
        backToKinds() {
            this.containerSearch = "";
            this.clearCascadeSelection();
            this.detailKind = null;
            this.step = "kind";
        },
        backToContainers() {
            this.step = "container";
            this.clearCascadeSelection();
        },
        isSearchedFormatSelected(format) {
            if (!format) {
                return false;
            }
            if (this.variant === "code") {
                return format.code === this.value;
            }
            return format.id == this.value;
        },
        onSearchedFormatSelect(format) {
            this.commitFormat(format);
        },
        onContainerSelect(option) {
            this.selectedContainerKey = option.key;
            this.selectedQuantity = null;
            const units = this.$utils.unitsForContainer(
                this.filteredFormats,
                option.key,
            );
            this.selectedUnit = units.length === 1 ? units[0] : units[0] || null;
            const quantities = this.selectedUnit
                ? this.$utils.quantitiesForContainerAndUnit(
                    this.filteredFormats,
                    option.key,
                    this.selectedUnit,
                )
                : [];
            if (units.length === 1 && quantities.length === 1) {
                this.commitFormat(
                    this.$utils.resolveFormat(
                        this.filteredFormats,
                        option.key,
                        quantities[0],
                        this.selectedUnit,
                    ),
                );
                return;
            }
            this.step = "content";
        },
        onUnitSelect(unit) {
            this.selectedUnit = unit;
            this.selectedQuantity = null;
            const quantities = this.$utils.quantitiesForContainerAndUnit(
                this.filteredFormats,
                this.selectedContainerKey,
                unit,
            );
            if (quantities.length === 1) {
                this.onQuantitySelect(quantities[0]);
            }
        },
        onQuantitySelect(quantity) {
            this.selectedQuantity = quantity;
            const format = this.$utils.resolveFormat(
                this.filteredFormats,
                this.selectedContainerKey,
                quantity,
                this.selectedUnit,
            );
            this.commitFormat(format);
        },
        quantityChoiceLabel(quantity) {
            const format = this.$utils.resolveFormat(
                this.filteredFormats,
                this.selectedContainerKey,
                quantity,
                this.selectedUnit,
            );
            if (format) {
                return this.$utils.formatContentCaption(format) || String(quantity);
            }
            return this.selectedUnit
                ? `${quantity} ${this.selectedUnit}`
                : String(quantity);
        },
        isQuantitySelected(quantity) {
            return String(this.selectedQuantity) === String(quantity);
        },
        commitFormat(format) {
            if (!format) {
                return;
            }
            const emitted =
                this.variant === "code" ? format.code || "" : format.id;
            this.$emit("input", emitted);
            this.$emit("change", emitted);
            this.menuOpen = false;
        },
        onActionSelect(actionValue) {
            if (this.actions.some((action) => action.value === actionValue)) {
                this.$emit("input", actionValue);
                this.$emit("change", actionValue);
                this.menuOpen = false;
                return;
            }
            if (actionValue === FORMAT_SELECT_CREATE) {
                this.$emit("create-format");
                this.menuOpen = false;
                return;
            }
            if (actionValue === FORMAT_SELECT_CUSTOM) {
                this.$emit("input", FORMAT_SELECT_CUSTOM);
                this.$emit("change", FORMAT_SELECT_CUSTOM);
                this.menuOpen = false;
                return;
            }
            if (actionValue === FORMAT_SELECT_UNIT || actionValue === "") {
                const emitted = this.variant === "code" ? "" : null;
                this.$emit("input", emitted);
                this.$emit("change", emitted);
                this.menuOpen = false;
            }
        },
        onAppendClick(event) {
            if (this.clearable && this.hasValue) {
                event.stopPropagation();
                event.preventDefault();
                const emitted = this.variant === "code" ? "" : null;
                this.$emit("input", emitted);
                this.$emit("change", emitted);
                return;
            }
            this.menuOpen = !this.menuOpen;
        },
        onFieldKeydown(event) {
            this.$emit("keydown", event);
            if (event.key === "Escape") {
                this.menuOpen = false;
            }
        },
        closeMenu() {
            this.menuOpen = false;
        },
        focus() {
            const field = this.$refs.field;
            if (field?.focus) {
                field.focus();
            }
        },
        focusFormatSearch() {
            const focusInput = () => {
                if (!this.menuOpen) {
                    return true;
                }
                const field = this.$refs.formatSearch;
                const input =
                    field
                    && ((field.$refs && field.$refs.input)
                        || field.$el?.querySelector?.('input'));
                if (!input) {
                    return false;
                }
                input.focus();
                return document.activeElement === input;
            };

            this.$nextTick(() => {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        if (focusInput()) {
                            return;
                        }
                        window.setTimeout(focusInput, 50);
                    });
                });
            });
        },
        activateMenu() {
            this.menuOpen = true;
        },
    },
    i18n: {
        messages: {
            en: {
                defaultEntry: "Per unit",
                kindPiece: "Per unit",
                kindWeight: "Weight",
                kindVolume: "Volume",
                kindFormat: "Format",
                customFormat: "Customize",
                createFormat: "Create a format",
                noFormatFound: "No format found",
                pickContainer: "Container",
                pickUnit: "Unit",
                pickQuantity: "Capacity",
                searchFormat: "Search a format…",
                noQuantityForUnit: "No capacity for this unit",
            },
            fr: {
                defaultEntry: "À l'unité",
                kindPiece: "À l'unité",
                kindWeight: "Poids",
                kindVolume: "Volume",
                kindFormat: "Format",
                customFormat: "Personaliser",
                createFormat: "Créer un format",
                noFormatFound: "Aucun format trouvé",
                pickContainer: "Contenant",
                pickUnit: "Unité",
                pickQuantity: "Contenance",
                searchFormat: "Rechercher un format…",
                noQuantityForUnit: "Aucune contenance pour cette unité",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.format-select__list {
    max-height: 260px;
    overflow-y: auto;
}

.format-select__panel {
    overflow: hidden;
}
</style>
